import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ASSETS } from "../lib/assets";
import { resolveImageUrl } from "../lib/adminApi";
import { CTA } from "../components/CTA";
import { useReveal } from "../lib/hooks";
import { ArrowLeft, ArrowRight, X, ZoomIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ----------------------------- Mock data ----------------------------- */
const CATEGORIES = {
  residencias: {
    slug: "residencias",
    label: "Residencias",
    intro:
      "Casas de autor, concebidas para una sola familia. Cada proyecto nace de su lugar, su luz y la forma única de habitar de quien lo elige.",
    quote:
      "Una casa debería sentirse como si solo hubiera podido existir para quien la habita.",
    projects: [
      {
        slug: "casa-tropico", number: "01", name: "Casa Trópico", location: "Barú, Colombia",
        hero: ASSETS.heroVilla,
        thumbs: [
          { label: "Exterior", image: ASSETS.proyectosResidencias },
          { label: "Interior", image: ASSETS.cierreInterior },
          { label: "Vista", image: ASSETS.pillarCalma },
          { label: "Detalle", image: ASSETS.pillarMateria },
        ],
      },
      {
        slug: "casa-coral", number: "02", name: "Casa Coral", location: "Barú, Colombia",
        hero: ASSETS.proyectosResidencias,
        thumbs: [
          { label: "Exterior", image: ASSETS.heroVilla },
          { label: "Interior", image: ASSETS.cierreInterior },
          { label: "Vista", image: ASSETS.pillarCalma },
          { label: "Detalle", image: ASSETS.pillarMateria },
        ],
      },
      {
        slug: "casa-cienaga", number: "03", name: "Casa Ciénaga", location: "Barú, Colombia",
        hero: ASSETS.pillarCalma,
        thumbs: [
          { label: "Exterior", image: ASSETS.heroVilla },
          { label: "Interior", image: ASSETS.cierreInterior },
          { label: "Vista", image: ASSETS.proyectosResidencias },
          { label: "Detalle", image: ASSETS.pillarMateria },
        ],
      },
      {
        slug: "casa-brisa", number: "04", name: "Casa Brisa", location: "Barú, Colombia",
        hero: ASSETS.cierreInterior,
        thumbs: [
          { label: "Exterior", image: ASSETS.heroVilla },
          { label: "Interior", image: ASSETS.proyectosResidencias },
          { label: "Vista", image: ASSETS.pillarCalma },
          { label: "Detalle", image: ASSETS.pillarMateria },
        ],
      },
    ],
  },
  enclaves: {
    slug: "enclaves",
    label: "Enclaves",
    intro:
      "Comunidades privadas donde la arquitectura ordena el territorio, el paisaje y la operación. Pequeños conjuntos pensados para una vida lenta, frente al Caribe colombiano.",
    quote:
      "Un enclave no es un proyecto: es la decisión de cuidar un territorio entero.",
    projects: [
      {
        slug: "enclave-baru", number: "01", name: "Enclave Barú", location: "Barú, Colombia",
        hero: ASSETS.proyectosEnclaves,
        thumbs: [
          { label: "Vista aérea", image: ASSETS.enclaveAerial },
          { label: "Laguna", image: ASSETS.pillarMateria },
          { label: "Vecindario", image: ASSETS.heroVilla },
          { label: "Detalle", image: ASSETS.baruRelieve },
        ],
      },
      {
        slug: "enclave-manglar", number: "02", name: "Enclave Manglar", location: "Barú, Colombia",
        hero: ASSETS.enclaveAerial,
        thumbs: [
          { label: "Vista aérea", image: ASSETS.proyectosEnclaves },
          { label: "Laguna", image: ASSETS.pillarMateria },
          { label: "Vecindario", image: ASSETS.heroVilla },
          { label: "Detalle", image: ASSETS.baruRelieve },
        ],
      },
      {
        slug: "enclave-coralino", number: "03", name: "Enclave Coralino", location: "Barú, Colombia",
        hero: ASSETS.heroVilla,
        thumbs: [
          { label: "Vista aérea", image: ASSETS.proyectosEnclaves },
          { label: "Laguna", image: ASSETS.enclaveAerial },
          { label: "Vecindario", image: ASSETS.pillarMateria },
          { label: "Detalle", image: ASSETS.baruRelieve },
        ],
      },
      {
        slug: "enclave-cocotera", number: "04", name: "Enclave Cocotera", location: "Barú, Colombia",
        hero: ASSETS.pillarMateria,
        thumbs: [
          { label: "Vista aérea", image: ASSETS.enclaveAerial },
          { label: "Laguna", image: ASSETS.proyectosEnclaves },
          { label: "Vecindario", image: ASSETS.heroVilla },
          { label: "Detalle", image: ASSETS.baruRelieve },
        ],
      },
    ],
  },
};

/* ----------------------------- Page ----------------------------- */
export default function ProyectosCategoria() {
  const { tipo } = useParams();
  const mockCat = CATEGORIES[tipo];
  const [remoteProjects, setRemoteProjects] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setRemoteProjects(null);
    axios
      .get(`${API}/projects`, { params: { category: tipo } })
      .then((r) => {
        if (cancelled) return;
        setRemoteProjects(r.data || []);
      })
      .catch(() => !cancelled && setRemoteProjects([]));
    return () => {
      cancelled = true;
    };
  }, [tipo]);

  // Effective category resolution:
  // - while remoteProjects is null  -> loading state (do not show mock to avoid flicker)
  // - remoteProjects has entries    -> use them
  // - remoteProjects is empty array -> fall back to mock category (legacy preview)
  const loading = remoteProjects === null;
  const cat =
    !loading && remoteProjects.length > 0 && mockCat
      ? {
          ...mockCat,
          projects: remoteProjects.map((p) => ({
            slug: p.slug,
            number: p.number,
            name: p.name,
            location: p.location,
            summary: p.summary || "",
            description: p.description || "",
            hero: resolveImageUrl(p.hero),
            thumbs: (p.thumbs || []).map((t) => ({
              label: t.label,
              image: resolveImageUrl(t.image),
            })),
          })),
        }
      : !loading
      ? mockCat
      : null;

  const [activeIdx, setActiveIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null); // { images: [{label,image}], index }

  const head = useReveal();
  const body = useReveal();
  const cta = useReveal();
  const list = useReveal({ threshold: 0.25 });

  useEffect(() => { setActiveIdx(0); }, [tipo, remoteProjects]);

  if (loading) {
    return (
      <main
        data-testid={`proyectos-${tipo}-loading`}
        className="bg-[var(--pc-bg)] min-h-screen flex items-center justify-center"
      >
        <div
          className="flex items-center gap-3 text-[var(--pc-text-soft)]"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 300,
            fontSize: 11,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
          }}
        >
          <span className="inline-block w-2 h-2 rounded-full bg-[var(--pc-bronze)] animate-pulse" />
          Cargando proyectos…
        </div>
      </main>
    );
  }

  if (!cat) {
    return (
      <main className="bg-[var(--pc-bg)] pt-44 pb-32">
        <div className="pc-container">
          <h1 className="headline-lg text-2xl">Categoría no encontrada.</h1>
          <div className="mt-10">
            <CTA testId="cat-back" to="/proyectos">Volver a proyectos</CTA>
          </div>
        </div>
      </main>
    );
  }

  const project = cat.projects[activeIdx];
  // gallery: hero + the 4 thumbs
  const gallery = [
    { label: project.name, image: project.hero },
    ...project.thumbs,
  ];

  const openLightbox = (idx) => setLightbox({ images: gallery, index: idx });

  return (
    <main data-testid={`proyectos-${tipo}-page`} className="bg-[var(--pc-bg)] relative">
      {/* ============ FULL-BLEED BG IMAGE (hero) + fade-to-black on sidebar ============ */}
      <section
        className="relative w-full overflow-hidden min-h-[88vh] lg:min-h-screen"
      >
        <div className="absolute inset-0">
          <img
            key={project.hero}
            src={project.hero}
            alt={project.name}
            className="absolute inset-0 w-full h-full object-cover"
            draggable="false"
          />
          {/* Black fade where the sidebar text lives */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(7,7,7,0.97) 0%, rgba(7,7,7,0.92) 22%, rgba(7,7,7,0.6) 36%, rgba(7,7,7,0.18) 52%, rgba(7,7,7,0) 70%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(7,7,7,0.5) 0%, transparent 16%, transparent 60%, rgba(7,7,7,0.92) 100%)",
            }}
          />
          {/* Extra darken on mobile so text is readable */}
          <div className="absolute inset-0 bg-[rgba(7,7,7,0.5)] md:hidden" />
        </div>

        {/* Content layout */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 min-h-[88vh] lg:min-h-screen">
          {/* ============ LEFT SIDEBAR ============ */}
          <aside
            data-testid="category-sidebar"
            className="lg:col-span-4 xl:col-span-3 px-6 md:px-10 lg:px-8 pt-28 md:pt-44 lg:pt-48 pb-12 md:pb-16 lg:pb-20 flex flex-col justify-center lg:justify-start min-h-[88vh] lg:min-h-0"
          >
            <div
              className="flex items-center gap-3 text-[var(--pc-text-soft)]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400, fontSize: 11,
                letterSpacing: "0.32em", textTransform: "uppercase",
              }}
            >
              <Link to="/proyectos" data-testid="breadcrumb-obra" className="hover:text-[var(--pc-bronze-bright)] transition-colors">
                Obra
              </Link>
              <span className="opacity-60">/</span>
              <span className="text-[var(--pc-bronze)]">{cat.label}</span>
            </div>

            <div className="mt-8">
              <h1
                key={`name-${project.slug}`}
                data-testid="active-project-name"
                className="headline-xl fade-up"
                style={{
                  fontSize: "clamp(20px, 1.7vw, 28px)",
                  letterSpacing: "0.04em", lineHeight: 1.2,
                  wordBreak: "keep-all",
                  hyphens: "none",
                }}
              >
                {project.name}
              </h1>
              <div className="h-px w-12 bg-[var(--pc-bronze)] mt-7 opacity-90" />
              <div
                key={`loc-${project.slug}`}
                className="mt-5 text-[var(--pc-bronze)] fade-up d1"
                style={{
                  fontFamily: "Inter, sans-serif", fontWeight: 400,
                  fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase",
                }}
              >
                {project.location}
              </div>
            </div>

            {/* Desktop-only: intro, CTA, project list inside the sidebar */}
            <div className="hidden lg:block">
              <p
                className="body-soft mt-9 max-w-[340px]"
                style={{ fontSize: 14.5 }}
              >
                {cat.intro}
              </p>

              <div className="mt-12">
                <CTA testId="category-cta" to="/contacto">
                  Conversemos sobre tu proyecto
                </CTA>
              </div>

              <div className="mt-14 border-t border-[var(--pc-line)]" data-testid="project-list">
                {cat.projects.map((p, i) => (
                  <button
                    key={p.slug}
                    data-testid={`project-item-${p.slug}`}
                    onClick={() => setActiveIdx(i)}
                    className="w-full text-left flex items-baseline gap-5 py-5 border-b border-[var(--pc-line)] group transition-all duration-500"
                  >
                    <span
                      className={`shrink-0 transition-colors duration-500 ${
                        activeIdx === i ? "text-[var(--pc-bronze)]" : "text-[var(--pc-text-soft)]"
                      }`}
                      style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 13, letterSpacing: "0.28em" }}
                    >
                      {p.number}
                    </span>
                    <span
                      className={`grow transition-colors duration-500 ${
                        activeIdx === i ? "text-[var(--pc-text)]" : "text-[var(--pc-text-soft)] group-hover:text-[var(--pc-text)]"
                      }`}
                      style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 13.5, letterSpacing: "0.18em", textTransform: "uppercase" }}
                    >
                      {p.name}
                    </span>
                    {activeIdx === i && <span className="h-px w-5 bg-[var(--pc-bronze)] mb-1" />}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* ============ MAIN — project name + arrows over the bg image ============ */}
          <div className="absolute inset-0 lg:relative lg:inset-auto lg:col-span-8 xl:col-span-9 flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-8 lg:pb-16 pt-6 md:pt-32 lg:pt-44 lg:min-h-screen pointer-events-none">
            {/* Clickable hero zone (opens lightbox at index 0) */}
            <button
              type="button"
              data-testid="hero-zoom"
              onClick={() => openLightbox(0)}
              aria-label={`Abrir galería de ${project.name}`}
              className="absolute inset-0 cursor-zoom-in group pointer-events-auto"
            >
              {/* zoom indicator that appears on hover */}
              <span className="absolute top-44 right-10 lg:top-48 lg:right-14 w-11 h-11 border border-[rgba(244,241,234,0.3)] flex items-center justify-center text-[var(--pc-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 backdrop-blur-sm bg-[rgba(7,7,7,0.3)]">
                <ZoomIn size={16} strokeWidth={1.2} />
              </span>
            </button>

            <div className="relative z-10 flex justify-end gap-6 pointer-events-none">
              <div className="flex items-center gap-5 pointer-events-auto">
                <span
                  className="text-[var(--pc-text-soft)] tabular"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 12, letterSpacing: "0.24em" }}
                >
                  {String(activeIdx + 1).padStart(2, "0")}
                  <span className="opacity-50"> / </span>
                  {String(cat.projects.length).padStart(2, "0")}
                </span>
                <div className="flex">
                  <button
                    data-testid="project-prev"
                    onClick={() => setActiveIdx((i) => (i - 1 + cat.projects.length) % cat.projects.length)}
                    aria-label="Anterior"
                    className="w-11 h-11 border border-[rgba(244,241,234,0.32)] flex items-center justify-center text-[var(--pc-text)] hover:text-[var(--pc-bronze-bright)] hover:border-[var(--pc-bronze)] transition-colors backdrop-blur-sm bg-[rgba(7,7,7,0.3)]"
                  >
                    <ArrowLeft size={16} strokeWidth={1.2} />
                  </button>
                  <button
                    data-testid="project-next"
                    onClick={() => setActiveIdx((i) => (i + 1) % cat.projects.length)}
                    aria-label="Siguiente"
                    className="w-11 h-11 border border-l-0 border-[rgba(244,241,234,0.32)] flex items-center justify-center text-[var(--pc-text)] hover:text-[var(--pc-bronze-bright)] hover:border-[var(--pc-bronze)] transition-colors backdrop-blur-sm bg-[rgba(7,7,7,0.3)]"
                  >
                    <ArrowRight size={16} strokeWidth={1.2} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROJECT SUMMARY + DESCRIPTION (per-project) ============ */}
      {(project.summary || project.description) && (
        <section
          data-testid="project-summary"
          className="border-t border-[var(--pc-line)] bg-[var(--pc-bg)]"
        >
          <div className="pc-container py-16 md:py-20 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              {/* Eyebrow / Summary */}
              <div className="lg:col-span-4">
                <div
                  className="text-[var(--pc-bronze)]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontSize: 11,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                  }}
                >
                  {cat.label} · {project.number}
                </div>
                {project.summary && (
                  <h3
                    key={`sum-${project.slug}`}
                    className="mt-5 text-[var(--pc-text)] fade-up"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 300,
                      fontSize: "clamp(20px, 1.9vw, 28px)",
                      letterSpacing: "0.04em",
                      lineHeight: 1.32,
                    }}
                  >
                    {project.summary}
                  </h3>
                )}
                <div className="h-px w-10 bg-[var(--pc-bronze)] mt-7 opacity-90" />
              </div>

              {/* Description */}
              {project.description && (
                <div className="lg:col-span-7 lg:col-start-6">
                  <p
                    key={`desc-${project.slug}`}
                    className="body-soft fade-up d1"
                    style={{
                      fontSize: 16.5,
                      lineHeight: 1.78,
                      maxWidth: 640,
                    }}
                  >
                    {project.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============ THUMBNAIL STRIP — clickable gallery ============ */}
      <section
        className="grid grid-cols-2 md:grid-cols-4 border-t border-[var(--pc-line)]"
        data-testid="project-thumbs"
      >
        {project.thumbs.map((t, i) => (
          <Thumb
            key={`${project.slug}-${i}`}
            label={t.label}
            image={t.image}
            onOpen={() => openLightbox(i + 1)}
          />
        ))}
      </section>

      {/* ============ MOBILE-ONLY: intro, CTA, project list (sidebar content) ============ */}
      <section
        data-testid="category-mobile-list"
        className="lg:hidden bg-[var(--pc-bg)] px-6 pt-12 pb-14 border-t border-[var(--pc-line)]"
      >
        <p
          className="body-soft"
          style={{ fontSize: 14.5 }}
        >
          {cat.intro}
        </p>

        <div className="mt-10">
          <CTA testId="category-cta-mobile" to="/contacto">
            Conversemos sobre tu proyecto
          </CTA>
        </div>

        {cat.projects.length > 1 && (
          <div className="mt-12 border-t border-[var(--pc-line)]" data-testid="project-list-mobile">
            {cat.projects.map((p, i) => (
              <button
                key={p.slug}
                data-testid={`project-item-m-${p.slug}`}
                onClick={() => {
                  setActiveIdx(i);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="w-full text-left flex items-baseline gap-5 py-7 border-b border-[var(--pc-line)] group transition-all duration-500"
              >
                <span
                  className={`shrink-0 transition-colors duration-500 ${
                    activeIdx === i ? "text-[var(--pc-bronze)]" : "text-[var(--pc-text-soft)]"
                  }`}
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 13, letterSpacing: "0.28em" }}
                >
                  {p.number}
                </span>
                <span
                  className={`grow transition-colors duration-500 ${
                    activeIdx === i ? "text-[var(--pc-text)]" : "text-[var(--pc-text-soft)] group-hover:text-[var(--pc-text)]"
                  }`}
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 13.5, letterSpacing: "0.18em", textTransform: "uppercase" }}
                >
                  {p.name}
                </span>
                {activeIdx === i && <span className="h-px w-5 bg-[var(--pc-bronze)] mb-1" />}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ============ Quote band ============ */}
      <section data-testid="category-quote" className="border-t border-[var(--pc-line)] bg-[var(--pc-bg-soft)]">
        <div className="pc-container py-14 md:py-16">
          <p
            className="text-[var(--pc-bronze)] max-w-[840px]"
            style={{
              fontFamily: "Inter, sans-serif", fontWeight: 300,
              fontSize: 12.5, letterSpacing: "0.28em",
              textTransform: "uppercase", lineHeight: 1.7,
            }}
          >
            {cat.quote}
          </p>
        </div>
      </section>

      {/* ============ Lightbox ============ */}
      {lightbox && (
        <Lightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </main>
  );
}

/* ----------------------------- Thumb ----------------------------- */
function Thumb({ label, image, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`thumb-${label.toLowerCase()}`}
      className="relative img-wrap group cursor-zoom-in"
      style={{ aspectRatio: "1/1" }}
    >
      <img src={image} alt={label} className="absolute inset-0 w-full h-full object-cover" />
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(180deg, transparent 55%, rgba(7,7,7,0.88) 100%)",
        }}
      />
      <span className="absolute top-4 right-4 w-9 h-9 border border-[rgba(244,241,234,0.35)] flex items-center justify-center text-[var(--pc-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 backdrop-blur-sm bg-[rgba(7,7,7,0.3)]">
        <ZoomIn size={14} strokeWidth={1.2} />
      </span>
      <div
        className="absolute left-0 right-0 bottom-0 px-5 py-4 text-[var(--pc-text)] text-left"
        style={{
          fontFamily: "Inter, sans-serif", fontWeight: 400,
          fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase",
        }}
      >
        {label}
      </div>
    </button>
  );
}

/* ----------------------------- Lightbox ----------------------------- */
function Lightbox({ images, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const total = images.length;

  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, next, prev]);

  const current = images[idx];

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[80] bg-[rgba(7,7,7,0.96)] backdrop-blur-md flex flex-col"
      onClick={onClose}
    >
      {/* top bar */}
      <div className="flex items-center justify-between px-6 md:px-10 py-5 z-10">
        <div
          className="text-[var(--pc-text-soft)] tabular"
          style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 12, letterSpacing: "0.28em" }}
        >
          {String(idx + 1).padStart(2, "0")}
          <span className="opacity-50"> / </span>
          {String(total).padStart(2, "0")}
        </div>
        <button
          data-testid="lightbox-close"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="Cerrar"
          className="inline-flex items-center gap-3 text-[var(--pc-text)] hover:text-[var(--pc-bronze-bright)] transition-colors"
          style={{
            fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 11.5,
            letterSpacing: "0.32em", textTransform: "uppercase",
          }}
        >
          <span>Cerrar</span>
          <X size={20} strokeWidth={1.2} />
        </button>
      </div>

      {/* image stage */}
      <div className="relative flex-1 flex items-center justify-center px-6 md:px-16 py-4">
        <img
          key={current.image}
          src={current.image}
          alt={current.label}
          className="max-w-full max-h-[80vh] object-contain shadow-2xl fade-up"
          onClick={(e) => e.stopPropagation()}
          draggable="false"
        />

        <button
          data-testid="lightbox-prev"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Anterior"
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-[rgba(244,241,234,0.32)] flex items-center justify-center text-[var(--pc-text)] hover:text-[var(--pc-bronze-bright)] hover:border-[var(--pc-bronze)] transition-colors backdrop-blur-sm bg-[rgba(7,7,7,0.5)]"
        >
          <ArrowLeft size={18} strokeWidth={1.2} />
        </button>
        <button
          data-testid="lightbox-next"
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Siguiente"
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-[rgba(244,241,234,0.32)] flex items-center justify-center text-[var(--pc-text)] hover:text-[var(--pc-bronze-bright)] hover:border-[var(--pc-bronze)] transition-colors backdrop-blur-sm bg-[rgba(7,7,7,0.5)]"
        >
          <ArrowRight size={18} strokeWidth={1.2} />
        </button>
      </div>

      {/* caption */}
      <div className="px-6 md:px-10 py-6 text-center" onClick={(e) => e.stopPropagation()}>
        <div
          className="text-[var(--pc-text)]"
          style={{
            fontFamily: "Inter, sans-serif", fontWeight: 300,
            fontSize: 13, letterSpacing: "0.28em", textTransform: "uppercase",
          }}
        >
          {current.label}
        </div>
      </div>
    </div>
  );
}
